import { TViewCommonOption, TWidgetCommonOption, TRowComponentProps, TWidget } from '@libs/types';
import { CoreButtonView, CoreImageView, CoreTextView, type TCoreButtonStyleProps, type TCoreButtonViewProps, type TCoreTextStyleProps } from '@core/widgets';
import React, { ChangeEvent, MouseEvent, MouseEventHandler } from 'react';
import { TSelectorRegularData } from '../../../types/data';
import { TRowOption, TSelectorRegularOption } from '../../../types/options';
import { TextStyle } from '../../../types/styles';
import { getHref } from '../../Image/imageUtils';
import { VisualView } from '../../Image/VisualView';
import { Switcher } from '../../Checkbox/Switcher';
import styles from './styles/rowRegular.module.css';

export type TRowRegularStyleProps = Pick<TRowOption & TSelectorRegularOption, 'margin' | 'isDisabled' | 'isHide'>;

export type TRowRegularViewProps = {
    widget?: TWidget<TSelectorRegularData, TRowOption & TSelectorRegularOption>;
    uid?: TWidget<TWidgetCommonOption>['uid'];
    title?: string;
    subtitle?: string;
    metaText?: string;
    value?: boolean;
    iconName?: string;
    flagIcon?: string;
    avatar?: string;
    iconHierarchy?: TRowOption['iconHierarchy'];
    iconVariant?: TRowOption['iconVariant'];
    metaVariant?: TRowOption['metaVariant'];
    metaHierarchy?: TRowOption['metaHierarchy'];
    variant?: TSelectorRegularOption['variant'];
    margin?: TWidgetCommonOption['margin'];
    isDisabled?: boolean;
    isHide?: boolean;
    handleClick: ((e: MouseEvent | ChangeEvent) => void) | MouseEventHandler<HTMLButtonElement>;
    parentStyleNames?: TViewCommonOption['parentStyleNames'];
} & Pick<TRowComponentProps, 'isDisabled' | 'isHide'>;

const CONTAINER_STYLE_PROPS: TCoreButtonStyleProps = {
    main: 'var(--color-white)',
    onMain: 'var(--color-black)',
    fillContainer: true,
    borderRadius: 0,
    padding: { top: 16, right: 24, bottom: 16, left: 24 },
    cursorDisabled: 'default',
    textAlign: 'start',
    height: 72,
    disabled: {
        main: 'transparent',
        onMain: 'var(--color-grey_40_lc)',
    },
    hovered: {
        main: 'var(--color-grey_8_lc)',
    },
    pressed: {
        main: 'var(--color-grey_8_lc)',
    },
};

const TITLE_STYLE_PROPS: TCoreTextStyleProps = {
    ...TextStyle.main,
    main: 'var(--color-black)',
    disabled: {
        main: 'var(--color-grey_40_lc)',
    },
};

const SUBTITLE_STYLE_PROPS: TCoreTextStyleProps = {
    ...TextStyle.secondary,
    main: 'var(--color-grey_72_lc)',
    margin: { top: 4 },
    disabled: {
        main: 'var(--color-grey_40_lc)',
    },
};

const META_STYLE_PROPS_SECONDARY: TCoreTextStyleProps = {
    ...TextStyle.secondary,
    main: 'var(--color-grey_72_lc)',
    disabled: {
        main: 'var(--color-grey_40_lc)',
    },
};

const META_STYLE_PROPS_PRIMARY: TCoreTextStyleProps = {
    ...TextStyle.main,
    main: 'var(--color-black)',
    disabled: {
        main: 'var(--color-grey_40_lc)',
    },
};

export const RowRegularView = ({
    widget,
    uid,
    title,
    subtitle,
    metaText,
    value,
    iconName,
    flagIcon,
    avatar,
    iconHierarchy,
    iconVariant,
    metaVariant,
    metaHierarchy,
    variant,
    margin,
    isDisabled,
    isHide,
    handleClick,
    parentStyleNames = [],
}: TRowRegularViewProps) => {
    const resolvedUid = widget?.uid ?? uid ?? '';
    const resolvedTitle = widget?.data?.messages?.title ?? title;
    const resolvedSubtitle = widget?.data?.messages?.subtitle ?? subtitle;
    const resolvedMetaText = widget?.data?.messages?.metaText ?? metaText;
    const resolvedValue = widget?.data?.value ?? value ?? false;
    const resolvedIconName = widget?.options?.iconName ?? iconName;
    const resolvedFlagIcon = widget?.options?.flagIcon ?? flagIcon;
    const resolvedAvatar = widget?.options?.avatar ?? avatar;
    const resolvedIconHierarchy = widget?.options?.iconHierarchy ?? iconHierarchy;
    const resolvedIconVariant = widget?.options?.iconVariant ?? iconVariant;
    const resolvedMetaVariant = widget?.options?.metaVariant ?? metaVariant;
    const resolvedMetaHierarchy = widget?.options?.metaHierarchy ?? metaHierarchy;
    const resolvedVariant = (widget?.options?.variant ?? variant) as TSelectorRegularOption['variant'];
    const resolvedMargin = widget?.options?.margin ?? margin;

    const resolvedWidget: TCoreButtonViewProps['widget'] =
        (widget as unknown as TCoreButtonViewProps['widget']) ?? ({ uid: resolvedUid, data: {}, options: {} } as TCoreButtonViewProps['widget']);

    const hasLeadingIcon = !resolvedVariant && (resolvedIconName || resolvedAvatar || resolvedFlagIcon);
    const effectiveMetaHierarchy = resolvedMetaHierarchy ?? 'secondary';
    const metaStyleProps = effectiveMetaHierarchy === 'secondary' ? META_STYLE_PROPS_SECONDARY : META_STYLE_PROPS_PRIMARY;

    const renderTrailing = () => {
        if (resolvedVariant === 'SWITCHER') {
            return (
                <div onClick={(e) => e.stopPropagation()}>
                    <Switcher
                        isWidget={false}
                        uid={`right_selector_regular_${resolvedUid}`}
                        isDisabled={isDisabled}
                        value={resolvedValue}
                        handleChange={handleClick as (e: React.ChangeEvent<HTMLInputElement>) => void}
                    />
                </div>
            );
        }
        if (resolvedVariant === 'CHECK' && resolvedValue) {
            return (
                <CoreImageView
                    src={getHref('ic_check')}
                    isDisabled={isDisabled}
                    parentStyleNames={[...parentStyleNames, 'rowRegularCheck']}
                    styleProps={{ width: 24, height: 24, iconColor: 'var(--color-purple_100)' }}
                />
            );
        }
        if (resolvedMetaText) {
            void resolvedMetaVariant;
            return (
                <CoreTextView
                    uid={'meta-' + resolvedUid}
                    text={resolvedMetaText}
                    as="span"
                    isDisabled={isDisabled}
                    parentStyleNames={parentStyleNames}
                    styleName="rowRegularMeta"
                    styleProps={metaStyleProps}
                />
            );
        }
        return null;
    };

    return (
        <CoreButtonView
            widget={resolvedWidget}
            isDisabled={isDisabled}
            isHide={isHide}
            styleProps={{
                ...CONTAINER_STYLE_PROPS,
                margin: resolvedMargin,
            }}
            parentStyleNames={parentStyleNames}
            styleName="rowRegular"
            onClick={handleClick as MouseEventHandler<HTMLButtonElement>}
        >
            {hasLeadingIcon && (
                <VisualView
                    uid={`icon-${resolvedUid}`}
                    iconName={resolvedIconName}
                    avatar={resolvedAvatar}
                    flagIcon={resolvedFlagIcon}
                    iconHierarchy={resolvedIconHierarchy}
                    iconVariant={resolvedIconVariant}
                    size={'small'}
                    isDisabled={isDisabled}
                    rightMargin
                    parentStyleNames={[...parentStyleNames, 'rowRegularIcon']}
                />
            )}
            <div className={styles.rowContent}>
                {resolvedTitle && (
                    <CoreTextView
                        uid={'title-' + resolvedUid}
                        text={resolvedTitle}
                        as="span"
                        isDisabled={isDisabled}
                        parentStyleNames={parentStyleNames}
                        styleName="rowRegularTitle"
                        styleProps={TITLE_STYLE_PROPS}
                    />
                )}
                {resolvedSubtitle && (
                    <CoreTextView
                        uid={'subtitle-' + resolvedUid}
                        text={resolvedSubtitle}
                        as="span"
                        isDisabled={isDisabled}
                        parentStyleNames={parentStyleNames}
                        styleName="rowRegularSubtitle"
                        styleProps={SUBTITLE_STYLE_PROPS}
                    />
                )}
            </div>
            {renderTrailing()}
        </CoreButtonView>
    );
};
